import { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  Checkbox,
  Button,
  Paper,
  Grid,
  Alert,
  Snackbar,
  Divider,
  Stack
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';
import { UserContext } from '../Context';
import { ReportContext } from '../Context';
import SettingsFieldDisplay from '../tool/SettingsFieldDisplay';
import GetUserField from '../tool/GetUserField';
import { logAction, formatReportNumber, LOG_ACTIONS } from '../../utils/logClient';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

function AddReport({ prefilledNumber = '', prefilledYear = '' }) {
  const { user } = useContext(UserContext);
  const { pickReport } = useContext(ReportContext);
  const [reportType, setReportType] = useState('initial');
  const [indiceValue, setIndiceValue] = useState('');
  const [isLimited, setIsLimited] = useState(false);
  const [isRestrictionForced, setIsRestrictionForced] = useState(false);
  const [unitValue, setUnitValue] = useState('');
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isUnitLoaded, setIsUnitLoaded] = useState(false);
  const [numberValue, setNumberValue] = useState(prefilledNumber);
  const [yearValue, setYearValue] = useState(prefilledYear);
  const [openSuccessSnackbar, setOpenSuccessSnackbar] = useState(false);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);

  const currentYear = new Date().getFullYear();

  // Récupérer le paramètre de restriction par défaut depuis les settings
  useEffect(() => {
    const fetchDefaultRestriction = async () => {
      try {
        const response = await axios.get('/api/settings');
        if (response.data) {
          // Nouveau format avec restrictionMode
          if (response.data.restrictionMode) {
            if (response.data.restrictionMode === 'always') {
              setIsLimited(true);
              setIsRestrictionForced(true);
            } else if (response.data.restrictionMode === 'default') {
              setIsLimited(true);
              setIsRestrictionForced(false);
            } else {
              setIsLimited(false);
              setIsRestrictionForced(false);
            }
          } 
          // Ancien format pour compatibilité arrière
          else if (response.data.defaultRestricted !== undefined) {
            setIsLimited(response.data.defaultRestricted);
            setIsRestrictionForced(false);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des paramètres de restriction:', error);
      }
    };
    fetchDefaultRestriction();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = sessionStorage.getItem('authToken');
      const response = await axios.get('/api/user', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const sortedUsers = response.data.sort((a, b) =>
        a.lastname.toUpperCase().localeCompare(b.lastname.toUpperCase())
      );
      setUsers(sortedUsers);
    } catch (error) {
      setErrorMessage('Erreur lors du chargement des utilisateurs.');
      setOpenErrorSnackbar(true);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (currentUser && !selectedUsers.some((u) => u.matricule === currentUser.matricule)) {
      setSelectedUsers([currentUser]);
    }
  }, [currentUser]);

  const handleReportTypeChange = (e) => {
    const newType = e.target.value;
    setReportType(newType);
    if (newType === 'subsequent') {
      setIndiceValue('__');
    } else {
      setIndiceValue('');
    }
  };

  const handleIndiceChange = (e) => {
    if (reportType !== 'subsequent') {
      const value = e.target.value.replace(/\D/g, '');
      setIndiceValue(value);
    }
  };

  const handleLimitedChange = (e) => {
    setIsLimited(e.target.checked);
  };

  const filterOptions = (options, { inputValue }) => {
    const inputLower = inputValue.toLowerCase();
    return options.filter((option) => {
      const fullName = `${option.lastname.toUpperCase()} ${option.firstname} (${option.rank})`;
      return fullName.toLowerCase().includes(inputLower);
    });
  };

  const handleUserSelectionChange = (event, newValue) => {
    if (currentUser) {
      const includesCurrentUser = newValue.some((u) => u.matricule === currentUser.matricule);
      if (!includesCurrentUser) {
        setSelectedUsers([currentUser, ...newValue]);
      } else {
        setSelectedUsers(newValue);
      }
    } else {
      setSelectedUsers(newValue);
    }
  };

  const handleUnitChange = (e) => {
    setUnitValue(e.target.value);
    setIsUnitLoaded(true);
  };

  const handleNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setNumberValue(value);
  };

  const handleYearChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setYearValue(value);
  };

  const handleCancel = () => {
    pickReport(null);
  };

  const handleCloseSnackbar = () => {
    setOpenSuccessSnackbar(false);
    setOpenErrorSnackbar(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    const yearInput = yearValue;
    const yearNum = parseInt(yearInput, 10);
    const indiceInput = indiceValue;
    const unitInput = unitValue;
    const numberInput = numberValue;

    if (reportType === 'initial' && (!indiceInput || indiceInput.trim() === '')) {
      setErrorMessage('L\'indice est obligatoire pour un procès-verbal initial.');
      setOpenErrorSnackbar(true);
      return;
    }
    if (!unitInput || unitInput.trim() === '') {
      setErrorMessage('Le service responsable est obligatoire.');
      setOpenErrorSnackbar(true);
      return;
    }
    if (isNaN(yearNum) || yearNum < 2000 || yearNum > currentYear) {
      setErrorMessage(`L'année doit être entre 2000 et ${currentYear}`);
      setOpenErrorSnackbar(true);
      return;
    }
    if (!numberInput || numberInput.trim() === '') {
      setErrorMessage('Le numéro du procès-verbal est obligatoire.');
      setOpenErrorSnackbar(true);
      return;
    }
    if (numberInput.length !== 6) {
      setErrorMessage('Le numéro du procès-verbal doit contenir exactement 6 chiffres.');
      setOpenErrorSnackbar(true);
      return;
    }

    const formData = {
      reportType,
      indice: indiceInput,
      judicialDistrict: document.getElementById('shortJudicialDistrict')?.value || '',
      judicialDivision: document.getElementById('judicialDivision')?.value || '',
      number: numberInput,
      year: yearInput,
      unit: unitInput,
      access: {
        type: isLimited ? 'restricted' : 'public',
        users: isLimited ? selectedUsers.map((u) => u.matricule) : [],
      },
      createdBy: user,
      createdAt: new Date().toISOString(),
      sections: [],
      chat: [],
    };

    try {
      const token = sessionStorage.getItem('authToken');
      const response = await axios.post('/api/report', formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccessMessage(`Document pour le procès-verbal ${response.data.number}/${response.data.year} créé avec succès !`);
      setOpenSuccessSnackbar(true);

      // Log de la création du rapport
      logAction(LOG_ACTIONS.REPORT_CREATE, formatReportNumber(response.data.number, response.data.year), {
        type: reportType === 'initial' ? 'initial' : 'subséquent',
        indice: formData.indice,
        unit: formData.unit,
        restricted: isLimited,
        usersWithAccess: isLimited ? selectedUsers.length : 'tous'
      });

      // Ajout à LastReports.json
      await axios.post(
        '/api/lastreports/add',
        {
          indice: formData.indice,
          number: formData.number,
          year: formData.year,
          restricted: formData.access.type,
          createdBy: formData.createdBy,
          createdAt: formData.createdAt,
          reportType: formData.reportType,
          unit: formData.unit,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Délai pour laisser le temps au serveur de traiter la création avant la navigation
      setTimeout(() => {
        pickReport({ number: response.data.number, year: response.data.year, new: false });
      }, 100);

      e.target.reset();
      setReportType('initial');
      setIndiceValue('');
      // Ne pas décocher si la restriction est forcée
      if (!isRestrictionForced) {
        setIsLimited(false);
      }
      setUnitValue('');
      setNumberValue('');
      setYearValue('');
      setSelectedUsers(currentUser ? [currentUser] : []);
      setIsUnitLoaded(false);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Erreur lors de la création du document.');
      setOpenErrorSnackbar(true);
    }
  };

  return (
    <Box component={Paper} elevation={3} sx={{ p: 4, maxWidth: 900, mx: 'auto', mt: 3, mb: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main', fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
        <AddCircleOutline sx={{ mr: 1 }} fontSize="large" />
        Création d'un nouveau document
      </Typography>
      
      <Divider sx={{ my: 2 }} />

      <GetUserField
        fieldName="lastname"
        onLoad={() => {
          const fetchCurrentUser = async () => {
            try {
              const token = sessionStorage.getItem('authToken');
              const response = await axios.get('/api/user', {
                headers: { Authorization: `Bearer ${token}` },
              });
              const foundUser = response.data.find((u) => u.matricule === user);
              if (foundUser) setCurrentUser(foundUser);
            } catch (err) {
              setErrorMessage('Erreur lors de la récupération de l\'utilisateur connecté.');
              setOpenErrorSnackbar(true);
            }
          };
          fetchCurrentUser();
        }}
      />

      <Box component="form" onSubmit={handleSubmit} autoComplete="off" sx={{ mt: 2 }}>
        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <RadioGroup
            row
            name="type-report"
            value={reportType}
            onChange={handleReportTypeChange}
          >
            <FormControlLabel value="initial" control={<Radio />} label="Procès-verbal initial" />
            <FormControlLabel value="subsequent" control={<Radio />} label="Procès-verbal subséquent" />
          </RadioGroup>
        </FormControl>

        <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
          <Grid item>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box component="span">
                <SettingsFieldDisplay fieldName="shortJudicialDistrict" />
              </Box>
              <Box component="span" sx={{ mx: 1 }}>.</Box>
              <TextField
                id="indice"
                size="small"
                inputProps={{
                  style: { textAlign: 'center' },
                  minLength: 2,
                  maxLength: 2,
                }}
                placeholder="..."
                value={indiceValue}
                onChange={handleIndiceChange}
                disabled={reportType === 'subsequent'}
                required={reportType === 'initial'}
                sx={{ width: '60px' }}
              />
              <Box component="span" sx={{ mx: 1 }}>.</Box>
              <Box component="span">
                <SettingsFieldDisplay fieldName="judicialDivision" />
              </Box>
              <Box component="span" sx={{ mx: 1 }}>.</Box>
              <TextField
                id="number"
                size="small"
                inputProps={{
                  style: { textAlign: 'center' },
                  minLength: 6,
                  maxLength: 6,
                }}
                placeholder="..."
                value={numberValue}
                onChange={handleNumberChange}
                required
                sx={{ width: '110px' }}
              />
              <Box component="span" sx={{ mx: 1 }}>/</Box>
              <TextField
                id="year"
                size="small"
                inputProps={{
                  style: { textAlign: 'center' },
                  minLength: 4,
                  maxLength: 4,
                }}
                placeholder="..."
                value={yearValue}
                onChange={handleYearChange}
                required
                sx={{ width: '80px' }}
              />
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Service responsable"
              id="unit"
              value={unitValue}
              onChange={handleUnitChange}
              required
              variant="outlined"
              size="small"
            />
            {!isUnitLoaded && (
              <GetUserField
                fieldName="service"
                onLoad={(value) => {
                  setUnitValue(value);
                  setIsUnitLoaded(true);
                }}
              />
            )}
          </Grid>
        </Grid>

        <FormControlLabel
          control={
            <Checkbox
              checked={isLimited}
              onChange={handleLimitedChange}
              name="limited"
              disabled={isRestrictionForced}
            />
          }
          label={
            isRestrictionForced 
              ? "Restreindre l'accès (obligatoire)" 
              : "Restreindre l'accès"
          }
          sx={{ 
            mb: 2,
            ...(isRestrictionForced && {
              '& .MuiFormControlLabel-label': {
                color: 'text.secondary',
                fontStyle: 'italic'
              }
            })
          }}
        />

        {isLimited && (
          <Box sx={{ mb: 3 }}>
            <Autocomplete
              multiple
              id="checkboxes-tags-demo"
              options={users}
              disableCloseOnSelect
              value={selectedUsers}
              onChange={handleUserSelectionChange}
              getOptionLabel={(option) =>
                `${option.lastname.toUpperCase()} ${option.firstname} (${option.rank})`
              }
              filterOptions={filterOptions}
              isOptionEqualToValue={(option, value) => option.matricule === value.matricule}
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                const isCurrentUser = currentUser && option.matricule === currentUser.matricule;
                return (
                  <li key={option.matricule} {...optionProps}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                      disabled={isCurrentUser}
                    />
                    {option.lastname.toUpperCase()} {option.firstname} ({option.rank})
                    {isCurrentUser && (
                      <Typography component="span" sx={{ ml: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                        (Vous)
                      </Typography>
                    )}
                  </li>
                );
              }}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Collègues qui auront accès au document"
                  placeholder="Recherchez par nom ou grade"
                  variant="outlined"
                />
              )}
              ListboxProps={{ style: { maxHeight: 300 } }}
              noOptionsText="Aucun utilisateur trouvé"
            />
          </Box>
        )}

        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            onClick={handleCancel}
            startIcon={<CancelIcon />}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<DoneOutlineIcon />}
            size="large"
          >
            Générer le document
          </Button>
        </Stack>
      </Box>

      <Snackbar
        open={openSuccessSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" elevation={6} variant="filled">
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={openErrorSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" elevation={6} variant="filled">
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AddReport;